import React, { useEffect } from 'react';
import Tile from '../common/Tile';
import { Divider, Grid } from 'semantic-ui-react';
import { useTitleDispatch } from '../TitleContextProvider';
import { withTranslation, useTranslation } from 'react-i18next';
import { Inventory } from '../../routes';
import { compose } from 'redux';
import connect from 'react-redux/es/connect/connect';
import {
  isTSO,
  isPublic,
  isFireBrigade,
  isDSO,
  isChemicalSafetyExpert,
  isEnvironmentalExpert,
  isAdmin,
  isMedicalService,
  isHseDeveloper,
  isGlobalViewer,
} from '../../behavior/init/selectors';
import { defaultFilterColumn } from '../../constants';
import BSearch from '../BuildingSummary/BSearch';
import { dataViewActions } from '../../behavior/dataview';
import { inventoriesActions } from '../../behavior/inventories';
import FavoriteTiles from './FavoriteTiles';
import ForbiddenPage from '../../pages/ForbiddenPage';
import i18n from 'i18next';
import WarningTile from '../common/WarningTile';
import useUser from '@behavior/init/getUserV2';

const Home = ({
  history,
  getFavoriteDataViewList,
  favoriteDataViewList,
  getNonCheckedChemicals,
  nonCheckedChemicalsObj,
}) => {
  const { user } = useUser();
  const dispatch = useTitleDispatch();
  const { t } = useTranslation();
  useEffect(() => {
    dispatch({
      pageTitle: t('page.home.title'),
    });
  }, [i18n.language]);

  useEffect(() => {
    if (user !== undefined) {
      if (!isPublic(user)) getFavoriteDataViewList();
      else history.push('/accessRequest');

      const departmentList = [...user?.departments];
      getNonCheckedChemicals(
        user.personId,
        departmentList.join(';'),
        user.buildings?.length > 0 && user.buildings[0]
      );
    }
  }, [user]);

  const wrapWithParentheses = str => '(' + str + ')';

  const shouldSeeDepartmentChemicals = () =>
    isChemicalSafetyExpert(user) ||
    isEnvironmentalExpert(user) ||
    isFireBrigade(user) ||
    isAdmin(user) ||
    isMedicalService(user) ||
    isHseDeveloper(user) ||
    isDSO(user) ||
    isGlobalViewer(user);

  const shouldSeeBuildingChemicals = () =>
    isTSO(user) && user.buildings && user.buildings.length > 0;

  return (
    <>
      <Grid stackable columns={3} className="centered withTopMargin homeTiles">
        <Divider horizontal>{t(`home.search.buildings.records`)}</Divider>
        <Grid.Row>
          <BSearch width={16} />
        </Grid.Row>
        <Divider horizontal>{t(`home.shortcuts`)}</Divider>
        <Divider horizontal></Divider>
        <Grid.Row>
          {!isPublic(user) ? (
            <Tile
              id="myChemicals"
              title="home.option.my.inventory.records"
              onClick={() =>
                history.push({
                  pathname: `${Inventory.baseName}/overview/entity/`,
                  state: { userDefaultFilterProp: [defaultFilterColumn.GEN_CONTACTS] },
                })
              }
            />
          ) : (
            <ForbiddenPage showHomeButton={false} />
          )}
          {shouldSeeDepartmentChemicals() && (
            <Tile
              id="myDepChemicals"
              title="home.option.my.department.records"
              onClick={() =>
                history.push({
                  pathname: `${Inventory.baseName}/overview/entity/`,
                  state: { userDefaultFilterProp: [defaultFilterColumn.GEN_ORG_UNIT] },
                })
              }
            />
          )}
          {shouldSeeBuildingChemicals() && (
            <Tile
              id="tsoChemicals"
              title="home.option.my.buildings.records"
              onClick={() =>
                history.push({
                  pathname: `${Inventory.baseName}/overview/entity/`,
                  state: { userDefaultFilterProp: [defaultFilterColumn.GEN_LOCATION_NAME] },
                })
              }
            />
          )}
        </Grid.Row>
        <Grid.Row>
          {nonCheckedChemicalsObj?.personal > 0 && (
            <WarningTile
              id="myNonCheckedChemicals"
              color={'orange'}
              title="home.option.my.non.checked.inventory.records"
              warningText={wrapWithParentheses(nonCheckedChemicalsObj.personal)}
              onClick={() =>
                history.push({
                  pathname: `${Inventory.baseName}/overview/entity/`,
                  state: {
                    userDefaultFilterProp: [
                      defaultFilterColumn.GEN_CONTACTS,
                      defaultFilterColumn.GEN_IS_CHECK_VALID,
                    ],
                  },
                })
              }
            />
          )}
          {shouldSeeDepartmentChemicals() && nonCheckedChemicalsObj?.dso > 0 && (
            <WarningTile
              id="myDepNonCheckedChemicals"
              title="home.option.my.non.checked.department.records"
              warningText={wrapWithParentheses(nonCheckedChemicalsObj.dso)}
              onClick={() =>
                history.push({
                  pathname: `${Inventory.baseName}/overview/entity/`,
                  state: {
                    userDefaultFilterProp: [
                      defaultFilterColumn.GEN_ORG_UNIT,
                      defaultFilterColumn.GEN_IS_CHECK_VALID,
                    ],
                  },
                })
              }
            />
          )}
          {shouldSeeBuildingChemicals() && nonCheckedChemicalsObj?.tso > 0 && (
            <WarningTile
              id="tsoNonCheckedChemicals"
              title="home.option.my.non.checked.building.records"
              warningText={wrapWithParentheses(nonCheckedChemicalsObj.tso)}
              onClick={() =>
                history.push({
                  pathname: `${Inventory.baseName}/overview/entity/`,
                  state: {
                    userDefaultFilterProp: [
                      defaultFilterColumn.GEN_LOCATION_NAME,
                      defaultFilterColumn.GEN_IS_CHECK_VALID,
                    ],
                  },
                })
              }
            />
          )}
        </Grid.Row>
      </Grid>
      <FavoriteTiles favoriteDataViewList={favoriteDataViewList} />
    </>
  );
};

const mapStateToProps = state => ({
  favoriteDataViewList: state.dataView.favoriteDataViewList,
  nonCheckedChemicalsObj: state.inventories.nonCheckedChemicals,
});

const mapDispatchToProps = dispatch => ({
  getFavoriteDataViewList: () => dispatch(dataViewActions.getFavoriteDataViewList()),
  getNonCheckedChemicals: (personId, department, building) =>
    dispatch(inventoriesActions.getNonCheckedChemicals(personId, department, building)),
});

export default React.memo(
  compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(Home)
);
